import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import type {
  ExamType,
  ExamWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  NORMAL_WORKSHEET_BY_TYPE,
  usesMultiIndication,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  styled,
  Typography,
} from "@material-ui/core";
import { nanoid } from "nanoid";
import { type FC, useState } from "react";
import type { RxCollection } from "rxdb";
import {
  getFeasibleExamTypes,
  isFeasibleExamType,
} from "../../../_lib/dicom-preset-mapping";
import { EpocusImageGallery } from "../../../_lib/epocus-image-gallery";
import {
  EPOCUS_BORDER,
  EPOCUS_ORANGE,
  EPOCUS_ORANGE_HOVER,
  EPOCUS_PRIMARY_HOVER_BG,
  EPOCUS_RED,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import {
  ExamTypePicker,
  ExamTypePickerHint,
} from "../../../_lib/exam-type-picker";
import type { HydratedExamImage } from "../../../_lib/hooks/use-epocus-exam-images";
import { useEnabledExamTypes } from "../../../_lib/use-enabled-exam-types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Groups images by dicomPreset; images with no preset (null, undefined, or
 * empty/whitespace-only string) use a literal null key so they are not
 * rendered as a blank-labelled preset group.
 */
function groupByPreset(
  images: HydratedExamImage[],
): Map<string | null, HydratedExamImage[]> {
  const map = new Map<string | null, HydratedExamImage[]>();
  for (const img of images) {
    const trimmed = img.dicomPreset?.trim();
    const key = trimmed ? trimmed : null;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(img);
  }
  return map;
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const SectionCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1.5px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  overflow: "hidden",
});

const SummaryRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": { backgroundColor: EPOCUS_PRIMARY_HOVER_BG },
});

const ExpandedBody = styled(Box)({
  padding: "0 16px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  paddingTop: 14,
});

const Actions = styled(Box)({
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  marginTop: 4,
});

const AssignButton = styled(Button)({
  backgroundColor: EPOCUS_ORANGE,
  color: EPOCUS_WHITE,
  borderRadius: 8,
  fontSize: rem(13),
  fontWeight: 700,
  textTransform: "none",
  minHeight: 36,
  "&:hover": { backgroundColor: EPOCUS_ORANGE_HOVER },
});

const CancelBtn = styled(Button)({
  borderRadius: 8,
  fontSize: rem(13),
  fontWeight: 600,
  textTransform: "none",
  minHeight: 36,
  color: EPOCUS_TEXT_SECONDARY,
});

// ─── PresetGroupCard ──────────────────────────────────────────────────────────

/**
 * One collapsible card for a single preset group of unmatched images.
 * Shows a preset-aware exam-type picker and an "Assign to Exam" button.
 */
const PresetGroupCard: FC<{
  preset: string | null;
  images: HydratedExamImage[];
  encounterId: string | null | undefined;
  examCollection: RxCollection<EpocusExam> | null;
  userId: string;
}> = ({ preset, images, encounterId, examCollection, userId }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState<ExamType | null>(
    null,
  );
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const feasibleTypes = preset != null ? getFeasibleExamTypes(preset) : null;

  const enabledExamTypes = useEnabledExamTypes();
  const feasibleEnabled = feasibleTypes
    ? enabledExamTypes.filter((examType) =>
        isFeasibleExamType(examType, feasibleTypes),
      )
    : [];

  // Auto-select when there's a single feasible type
  const effectiveSelected =
    selectedExamType ??
    (feasibleEnabled.length === 1 ? feasibleEnabled[0] : null);

  const handleAssign = async () => {
    if (!effectiveSelected || !encounterId || !examCollection) return;
    setAssignError(null);
    setIsAssigning(true);

    try {
      const existing = await examCollection
        .findOne({ selector: { encounterId, examType: effectiveSelected } })
        .exec();

      if (existing?.signedAt && existing.signedByUserId !== userId) {
        setAssignError(
          "That exam's report is signed. Only the clinician who signed it can add images.",
        );
        return;
      }

      const examImages = images.map((img) => ({
        id: img.id,
        previewS3Key: img.previewS3Key,
        viewLabel: img.viewLabel,
        dicomPreset: img.dicomPreset ?? null,
        physicalDeltaMm: img.physicalDeltaMm ?? null,
      }));

      if (!existing) {
        const normalData = NORMAL_WORKSHEET_BY_TYPE[effectiveSelected];
        const emptyViews = Object.fromEntries(
          Object.keys(normalData.views).map((k) => [k, false]),
        );
        const now = new Date().toISOString();
        await examCollection.insert({
          id: nanoid(),
          encounterId,
          examType: effectiveSelected,
          status: "draft",
          signedAt: null,
          signedByUserId: null,
          nonBillable: false,
          nonBillableReason: null,
          amendments: [],
          worksheetData: JSON.parse(
            JSON.stringify({
              ...normalData,
              indication: usesMultiIndication(effectiveSelected) ? [] : null,
              views: emptyViews,
            }),
          ) as ExamWorksheetData,
          images: examImages,
          meta: {
            createdAt: now,
            createdByUserId: userId,
            updatedAt: now,
            updatedByUserId: userId,
          },
        });
      } else {
        // Use incrementalModify for an atomic read-modify-write so we base
        // the append on the latest document state, not the stale snapshot.
        await existing.incrementalModify((docData) => {
          const currentIds = new Set(docData.images.map((img) => img.id));
          const additions = examImages.filter((img) => !currentIds.has(img.id));
          if (additions.length === 0) return docData;
          return {
            ...docData,
            images: [...docData.images, ...additions],
            meta: {
              ...docData.meta,
              updatedAt: new Date().toISOString(),
              updatedByUserId: userId,
            },
          };
        });
      }

      // Collapse and reset after successful assignment
      setExpanded(false);
      setSelectedExamType(null);
    } catch (err) {
      console.error("Failed to assign images to exam", err);
      setAssignError("Failed to assign images. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <SectionCard>
      <SummaryRow
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography
            style={{
              fontSize: rem(15),
              fontWeight: 600,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {images.length} {images.length === 1 ? "image" : "images"}
          </Typography>
          <Typography
            style={{
              fontSize: rem(12),
              color: EPOCUS_TEXT_SECONDARY,
              fontWeight: 500,
            }}
          >
            · {preset ?? "Unknown preset"}
          </Typography>
        </Box>
        <Typography
          style={{
            fontSize: rem(18),
            color: EPOCUS_TEXT_SECONDARY,
            lineHeight: 1,
          }}
        >
          {expanded ? "∧" : "›"}
        </Typography>
      </SummaryRow>

      <Collapse in={expanded}>
        <ExpandedBody>
          <EpocusImageGallery
            images={images}
            selectedIndex={lightboxIndex}
            onImageClick={setLightboxIndex}
          />

          <ExamTypePickerHint
            dominantPreset={preset}
            feasibleTypes={feasibleTypes}
          />

          <ExamTypePicker
            allowedTypes={feasibleTypes}
            selected={effectiveSelected}
            onChange={setSelectedExamType}
          />

          {assignError && (
            <Typography
              style={{
                color: EPOCUS_RED,
                fontSize: rem(13),
                fontWeight: 500,
              }}
            >
              {assignError}
            </Typography>
          )}

          <Actions>
            <CancelBtn
              variant="text"
              onClick={() => {
                setExpanded(false);
                setSelectedExamType(null);
                setAssignError(null);
              }}
              disabled={isAssigning}
            >
              Cancel
            </CancelBtn>
            <AssignButton
              variant="contained"
              disableElevation
              disabled={
                !effectiveSelected ||
                isAssigning ||
                !encounterId ||
                !examCollection
              }
              onClick={handleAssign}
            >
              {isAssigning ? (
                <CircularProgress size={16} style={{ color: EPOCUS_WHITE }} />
              ) : (
                "Assign to Exam →"
              )}
            </AssignButton>
          </Actions>
        </ExpandedBody>
      </Collapse>
    </SectionCard>
  );
};

// ─── UnmatchedDicomSection ────────────────────────────────────────────────────

/**
 * PNE-213 / PNE-221: Inline section on the encounter page for imaging instances
 * that could not be auto-assigned to an exam (ambiguous or unknown DICOM presets).
 *
 * Images are grouped by dicomPreset so each distinct preset group gets its own
 * collapsible card with a tailored exam-type picker. This supports studies that
 * contain multiple different preset types (e.g. CHEST + ABDOMINAL from a single
 * GE VScan Air export).
 */
export const UnmatchedDicomSection: FC<{
  images: HydratedExamImage[];
  encounterId: string | null | undefined;
  examCollection: RxCollection<EpocusExam> | null;
  userId: string;
}> = ({ images, encounterId, examCollection, userId }) => {
  const groups = groupByPreset(images);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
      {[...groups.entries()].map(([preset, groupImages]) => (
        <PresetGroupCard
          key={preset == null ? "preset:null" : `preset:${preset}`}
          preset={preset}
          images={groupImages}
          encounterId={encounterId}
          examCollection={examCollection}
          userId={userId}
        />
      ))}
    </Box>
  );
};
